import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { SIDEBAR_COLLAPSED_KEY } from '../shared/nav-breakpoints'

/**
 * Persists user-chosen sidebar collapsed/expanded preference in `localStorage`
 * under {@link SIDEBAR_COLLAPSED_KEY}. Per Story 1.6:
 *
 * - Default at desktop width: expanded (false).
 * - Default at tablet width: collapsed (true) — NOT persisted (next visit reverts).
 * - localStorage access is wrapped in try/catch so private-browsing edge cases
 *   fall back silently to the breakpoint default.
 */
@Injectable({ providedIn: 'root' })
export class SidebarPreferencesService {
  private readonly collapsedSubject = new BehaviorSubject<boolean | undefined>(this.readFromStorage())
  readonly collapsed$ = this.collapsedSubject.asObservable()

  /** Current value (synchronous read, falls back to undefined when no preference is stored). */
  get collapsed(): boolean | undefined {
    return this.collapsedSubject.value
  }

  /**
   * Updates the in-memory state. `persist=true` also writes to localStorage
   * (used at desktop width); `persist=false` updates state only (used at tablet
   * width per Story 1.6 AC: 3).
   */
  setCollapsed(value: boolean, persist: boolean): void {
    this.collapsedSubject.next(value)
    if (persist) {
      this.writeToStorage(value)
    }
  }

  private readFromStorage(): boolean | undefined {
    try {
      const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      if (raw === null) return undefined
      return raw === 'true'
    } catch {
      return undefined
    }
  }

  private writeToStorage(value: boolean): void {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value))
    } catch {
      // Private browsing / disabled storage — silent fallback.
    }
  }
}
